import { z } from "zod";

const OpaqueIdSchema = z.uuid();
const RequestIdSchema = z.string().regex(/^req_[0-9a-f-]{36}$/u);

export const NotificationEventTypeSchema = z.enum([
  "routine_reconciliation_repaired",
  "routine_reconciliation_failed",
  "task_help_requested",
  "task_verification_waiting",
  "task_start_reminder",
  "task_stalled",
  "timer_finished",
  "notification_delivery_failed",
]);

export const NotificationDeepLinkRouteSchema = z.enum([
  "parent_today",
  "parent_routines",
  "parent_review",
  "child_today",
]);

export const NotificationPayloadSchema = z
  .object({
    missing_count: z.number().int().min(0).nullable().default(null),
    repaired_count: z.number().int().min(0).nullable().default(null),
    task_count: z.number().int().min(0).nullable().default(null),
    child_count: z.number().int().min(0).nullable().default(null),
    error_code: z.string().min(1).max(160).nullable().default(null),
  })
  .strict();

export const NotificationSchema = z
  .object({
    id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    event_type: NotificationEventTypeSchema,
    recipient_type: z.enum(["membership", "child"]),
    related_child_id: OpaqueIdSchema.nullable(),
    task_id: OpaqueIdSchema.nullable(),
    routine_id: OpaqueIdSchema.nullable(),
    reconciliation_run_id: OpaqueIdSchema.nullable(),
    channel: z.enum(["in_app", "web_push", "apns"]),
    status: z.enum(["pending", "delivered", "cancelled", "expired", "permanent_failure"]),
    not_before: z.number().int().nonnegative(),
    expires_at: z.number().int().positive(),
    deep_link: z
      .object({
        route: NotificationDeepLinkRouteSchema,
        target_id: OpaqueIdSchema.nullable(),
      })
      .strict(),
    quiet_hours_behavior: z.enum(["defer", "digest", "allow"]),
    policy_version: z.number().int().positive(),
    payload: NotificationPayloadSchema,
    read_at: z.number().int().nonnegative().nullable(),
    snoozed_until: z.number().int().nonnegative().nullable(),
    delivered_at: z.number().int().nonnegative().nullable(),
    version: z.number().int().positive(),
    created_at: z.number().int().nonnegative(),
    updated_at: z.number().int().nonnegative(),
  })
  .strict();

export const NotificationListResponseSchema = z
  .object({
    items: z.array(NotificationSchema).max(100),
    next_cursor: z.string().nullable(),
    request_id: RequestIdSchema,
  })
  .strict();

export const NotificationReadCommandRequestSchema = z
  .object({ expected_version: z.number().int().positive() })
  .strict();

export const NotificationSnoozeCommandRequestSchema = z
  .object({
    expected_version: z.number().int().positive(),
    snoozed_until: z.number().int().positive(),
  })
  .strict();

export const NotificationCommandResponseSchema = z
  .object({
    notification: NotificationSchema,
    command: z.enum(["read", "unread", "snooze"]),
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: RequestIdSchema,
  })
  .strict();

export const NotificationPreferenceEventTypeSchema = z.enum([
  "routine_reconciliation_repaired",
  "routine_reconciliation_failed",
  "task_help_requested",
  "task_verification_waiting",
  "task_start_reminder",
  "task_stalled",
  "timer_finished",
  "notification_delivery_failed",
]);

export const NotificationPreferenceSchema = z
  .object({
    id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    recipient_type: z.enum(["membership", "child"]),
    child_id: OpaqueIdSchema.nullable(),
    related_child_id: OpaqueIdSchema.nullable(),
    routine_id: OpaqueIdSchema.nullable(),
    channel: z.enum(["in_app", "web_push", "apns"]),
    event_types: z.array(NotificationPreferenceEventTypeSchema).min(1).max(8),
    quiet_hours_behavior: z.enum(["defer", "digest", "allow"]),
    batching_mode: z.enum(["immediate", "digest"]),
    enabled: z.boolean(),
    active_weekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7),
    active_from_local_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/u)
      .nullable(),
    active_until_local_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/u)
      .nullable(),
    digest_local_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/u)
      .nullable(),
    version: z.number().int().positive(),
    created_at: z.number().int().nonnegative(),
    updated_at: z.number().int().nonnegative(),
  })
  .strict();

export const NotificationPreferenceInputSchema = z
  .object({
    expected_version: z.number().int().positive().nullable(),
    recipient: z.discriminatedUnion("type", [
      z.object({ type: z.literal("membership") }).strict(),
      z.object({ type: z.literal("child"), child_id: OpaqueIdSchema }).strict(),
    ]),
    related_child_id: OpaqueIdSchema.nullable(),
    routine_id: OpaqueIdSchema.nullable(),
    channel: z.enum(["in_app", "web_push", "apns"]),
    event_types: z
      .array(NotificationPreferenceEventTypeSchema)
      .min(1)
      .max(8)
      .refine((values) => new Set(values).size === values.length, {
        message: "Notification event types must be unique.",
      }),
    quiet_hours_behavior: z.enum(["defer", "digest", "allow"]),
    batching_mode: z.enum(["immediate", "digest"]),
    enabled: z.boolean(),
    active_weekdays: z
      .array(z.number().int().min(0).max(6))
      .min(1)
      .max(7)
      .refine((values) => new Set(values).size === values.length, {
        message: "Active weekdays must be unique.",
      }),
    active_from_local_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/u)
      .nullable(),
    active_until_local_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/u)
      .nullable(),
    digest_local_time: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/u)
      .nullable(),
  })
  .strict()
  .superRefine((input, context) => {
    if ((input.active_from_local_time === null) !== (input.active_until_local_time === null)) {
      context.addIssue({
        code: "custom",
        path: ["active_from_local_time"],
        message: "Both active time boundaries are required together.",
      });
    }
    if (input.batching_mode === "digest" && input.digest_local_time === null) {
      context.addIssue({
        code: "custom",
        path: ["digest_local_time"],
        message: "Digest delivery time is required for digest batching.",
      });
    }
    if (input.recipient.type === "child" && input.related_child_id !== null) {
      context.addIssue({
        code: "custom",
        path: ["related_child_id"],
        message: "A child recipient already defines its child scope.",
      });
    }
    if (input.recipient.type === "child" && input.batching_mode === "digest") {
      context.addIssue({
        code: "custom",
        path: ["batching_mode"],
        message: "Child reminders must remain immediate and gentle.",
      });
    }
  });

export const NotificationPreferenceListResponseSchema = z
  .object({
    items: z.array(NotificationPreferenceSchema).max(500),
    request_id: RequestIdSchema,
  })
  .strict();

export const NotificationPreferenceMutationResponseSchema = z
  .object({
    preference: NotificationPreferenceSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: RequestIdSchema,
  })
  .strict();

export type Notification = z.infer<typeof NotificationSchema>;
export type NotificationEventType = z.infer<typeof NotificationEventTypeSchema>;
export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;
export type NotificationPreferenceInput = z.infer<typeof NotificationPreferenceInputSchema>;
